import {
  CalendarOutlined,
  CheckCircleFilled,
  LoadingOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import type { Deposit } from "../../../../../api/deposits";
import { depositReceiptStateOf } from "../../../../../lib/depositReceipt";
import { depositPickBlockOf } from "./billDeposit";
import { formatCents, parseMoneyToCents } from "../../../../../utils/money";

interface DepositPickerRowProps {
  deposit: Deposit;
  /** Сметката, към която се закача. */
  billId: number;
  isHighlighted: boolean;
  /** Закаченият в момента към тази сметка. */
  isAttachedHere: boolean;
  onPick: (deposit: Deposit) => void;
  /** „Издай бон" — само с правото „Зареждане с депозит"; иначе null. */
  onIssueReceipt: ((deposit: Deposit) => void) | null;
  /** Бонът на този депозит се издава в момента. */
  isIssuing: boolean;
}

/**
 * Един депозит в избора „Използвай депозит". Депозит без касов бон (заварен,
 * неуспял, неясен) е видим, но неактивен — с „Първо издайте бона на
 * депозита" и бутон „Издай бон" (BE отказва такъв депозит); закаченият към
 * друга маса — неактивен с „Закачен към маса N"; закаченият към тази — с
 * отметка. Редът е div, не button: „Издай бон" е истински <button> вътре.
 */
export function DepositPickerRow({
  deposit,
  billId,
  isHighlighted,
  isAttachedHere,
  onPick,
  onIssueReceipt,
  isIssuing,
}: DepositPickerRowProps) {
  const { t, i18n } = useTranslation();
  const pickBlock = depositPickBlockOf(deposit, billId);
  const isReady = pickBlock === null;
  const methodName =
    (i18n.language === "en" ? deposit.paymentMethodNameEn : deposit.paymentMethodNameBg) ?? "";
  const notReadyHint =
    pickBlock === "otherBill"
      ? t("depositAction.attachedElsewhere", { table: deposit.usedBillName ?? "" })
      : depositReceiptStateOf(deposit) === "card_charged"
        ? t("depositReceipt.notReadyCardCharged")
        : t("depositReceipt.notReadyHint");

  return (
    <div
      role="button"
      tabIndex={isReady ? 0 : -1}
      aria-disabled={!isReady}
      data-deposit-id={deposit.id}
      onClick={() => {
        if (isReady) onPick(deposit);
      }}
      className={`flex w-full items-center gap-3 rounded-[10px] border px-3.5 py-3 text-left transition-colors ${
        !isReady
          ? "cursor-not-allowed border-pos-line3 bg-pos-panel"
          : isHighlighted
            ? "cursor-pointer border-pos-accent bg-pos-marked"
            : "cursor-pointer border-pos-line3 bg-pos-panel hover:bg-pos-canvas"
      }`}
    >
      <span className={`flex min-w-0 flex-1 flex-col gap-0.5 ${isReady ? "" : "opacity-60"}`}>
        {/* Номерът на депозита (id-то) и на касовия му бон (Бон 1, щом го
            има) — само показване, не се редактират; търсачката намира и по двата. */}
        <span className="font-pos-mono text-[11.5px] font-bold text-pos-muted [font-variant-numeric:tabular-nums]">
          {t("closeTable.deposit.number", { id: deposit.id })}
          {deposit.receiptNumber &&
            ` · ${t("closeTable.deposit.receiptNumber", { number: deposit.receiptNumber })}`}
        </span>
        <span
          className={`flex min-w-0 items-center gap-1.5 text-sm font-semibold ${
            isHighlighted && isReady ? "text-pos-accent-ink" : "text-pos-ink"
          }`}
        >
          {isAttachedHere && <CheckCircleFilled className="shrink-0 !text-pos-accent" />}
          <span className="truncate">{deposit.name}</span>
        </span>
        {deposit.description && (
          <span className="truncate text-[12.5px] font-medium text-pos-soft2">
            {deposit.description}
          </span>
        )}
        <span className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11.5px] font-medium text-pos-muted">
          <span className="inline-flex items-center gap-1">
            <CalendarOutlined className="text-[11px]" />
            {deposit.validUntil
              ? t("closeTable.deposit.validUntil", {
                  date: dayjs(deposit.validUntil).format("DD.MM.YYYY"),
                })
              : t("closeTable.deposit.noExpiry")}
          </span>
          <span>
            {t("closeTable.deposit.takenOn", {
              date: dayjs(deposit.createdAt).format("DD.MM.YYYY"),
              method: methodName,
            })}
          </span>
        </span>
        {!isReady && (
          <span className="text-[12px] font-semibold text-pos-danger-ink">{notReadyHint}</span>
        )}
      </span>
      <span className="flex shrink-0 flex-col items-end gap-1.5">
        <span
          className={`font-pos-mono text-[15px] font-bold text-pos-ink [font-variant-numeric:tabular-nums] ${
            isReady ? "" : "opacity-60"
          }`}
        >
          {formatCents(parseMoneyToCents(deposit.amount) ?? 0)}{" "}
          <span className="text-[11px] text-pos-muted">EUR</span>
        </span>
        {pickBlock === "notReady" && onIssueReceipt && (
          <button
            type="button"
            disabled={isIssuing}
            onClick={(event) => {
              event.stopPropagation();
              onIssueReceipt(deposit);
            }}
            className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-pos-accent/40 bg-pos-card px-2.5 py-1.5 text-xs font-semibold text-pos-accent-ink transition-colors hover:border-pos-accent disabled:cursor-wait disabled:opacity-60"
          >
            {isIssuing ? <LoadingOutlined spin /> : <PrinterOutlined />}
            {t("depositReceipt.issue")}
          </button>
        )}
      </span>
    </div>
  );
}
